// The Connect flavour of a firm (docs/SYSTEM_MAP.md): Attract, Find or Network, the way it came in. Shown on firm and
// person pages and on reply and deal cards, with the playbook's pace and offer rule when the card needs them.
import type { Crm } from "../lib/crm";
import { FIRST_ASK_LABEL, FLAVOUR_HINT, FLAVOUR_LABEL, hoursText, type Flavour, type Playbook } from "../lib/connect";
import { fmtDateTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";

const TONE: Record<Flavour, string> = { attract: "info", find: "plain", network: "booked" };

export function FlavourChip({ flavour }: { flavour: Flavour | null | undefined }) {
  if (!flavour || !FLAVOUR_LABEL[flavour]) return null;
  return (
    <span className={`chip chip-${TONE[flavour]}`} title={FLAVOUR_HINT[flavour]}>
      {FLAVOUR_LABEL[flavour]}
    </span>
  );
}

/** One line under a card: the flavour, when the reply is due, what to ask for first and whether an offer may be made. */
export function PlaybookLine({ pb, due }: { pb: Playbook; due?: string | null }) {
  const by = due ?? pb.reply_due_by ?? null;
  return (
    <p className="small playbook-line">
      <FlavourChip flavour={pb.flavour} /> {FLAVOUR_HINT[pb.flavour]} Reply within {hoursText(pb.reply_within_hours)}
      {by ? ` (by ${fmtDateTime(by)})` : ""}; ask for {FIRST_ASK_LABEL[pb.first_ask].toLowerCase()}.{" "}
      {pb.may_offer ? "An offer may be made now." : `No offer yet: ${pb.offer_note}.`}
      {pb.introducer?.person || pb.introducer?.organisation ? ` Introduced by ${pb.introducer.person ?? pb.introducer.organisation}.` : ""}
    </p>
  );
}

/** Loads the playbook for a firm, or for something of the firm's, and shows the line; nothing while loading or when the
 *  firm has none (a firm not in this business, or a database without the playbooks yet). */
export function EntityPlaybook({ crm, organisation, entityType, entityId, due }: { crm: Crm; organisation?: string | null; entityType?: string | null; entityId?: string | null; due?: string | null }) {
  const pb = useAsync(() => (organisation || entityId ? crm.connectPlaybook({ organisation, entityType, entityId }).catch(() => null) : Promise.resolve(null)), [crm, organisation, entityType, entityId]);
  // Only for a firm in Connect (or back from it): a firm still in Find has no playbook yet.
  if (!pb.data || pb.data.stored === false) return null;
  return <PlaybookLine pb={pb.data} due={due} />;
}
